<template>
  <v-card outlined class="tokeva-danger-zone-card">
    <v-card-title
      class="py-2 text-subtitle-1 font-weight-bold d-flex justify-space-between align-center flex-wrap"
    >
      <div class="d-flex align-center flex-wrap mr-2">
        <v-icon left color="error">mdi-radius-outline</v-icon>
        <span class="font-weight-bold">Vaara-alueen taktinen mitoitus</span>
        <v-chip
          v-if="chemicalName"
          small
          color="orange darken-4"
          dark
          class="ml-2 font-weight-bold"
        >
          {{ chemicalName }} {{ unCode ? `(YK ${unCode})` : "" }}
        </v-chip>
      </div>

      <div class="d-flex align-center flex-wrap my-1">
        <!-- View mode toggle: Kaavio vs. Tilannekartta -->
        <v-btn-toggle v-model="activeView" mandatory dense class="mr-2">
          <v-btn value="diagram" small class="font-weight-medium">
            <v-icon left small>mdi-chart-donut</v-icon>
            Kaavio
          </v-btn>
          <v-btn
            value="map"
            small
            class="font-weight-medium"
            :color="hasActiveAlarm ? 'error' : ''"
          >
            <v-icon left small :color="hasActiveAlarm ? 'error' : 'primary'">
              mdi-map-marker-radius
            </v-icon>
            Tilannekartta
            <v-badge v-if="hasActiveAlarm" dot color="error" class="ml-1" />
          </v-btn>
        </v-btn-toggle>

        <!-- Leak size toggle with high contrast -->
        <v-btn-toggle v-model="leakType" mandatory dense class="my-1 leak-toggle-group">
          <v-btn
            value="small"
            small
            class="font-weight-bold leak-btn leak-btn-small"
            :class="{ 'leak-btn-active': leakType === 'small' }"
          >
            <v-icon left small>mdi-water-minus</v-icon>
            Pieni vuoto ({{ smallZoneLabel }})
          </v-btn>
          <v-btn
            value="large"
            small
            class="font-weight-bold leak-btn leak-btn-large"
            :class="{ 'leak-btn-active': leakType === 'large' }"
          >
            <v-icon left small>mdi-water-plus</v-icon>
            Suuri vuoto ({{ largeZoneLabel }})
          </v-btn>
        </v-btn-toggle>

        <!-- Custom dimensions edit button (behind a button as requested) -->
        <v-btn
          small
          outlined
          :color="isCustomDimensions ? 'warning darken-2' : 'primary'"
          class="font-weight-bold ml-2 my-1"
          title="Aseta omat vaara-alueen etäisyydet (PTJ:n mitoitus)"
          @click="openCustomDimensionsDialog"
        >
          <v-icon left small>{{ isCustomDimensions ? 'mdi-pencil-ruler' : 'mdi-ruler-square' }}</v-icon>
          {{ isCustomDimensions ? 'Omat etäisyydet (PTJ)' : 'Muokkaa etäisyyksiä' }}
        </v-btn>

        <!-- Fullscreen expand button for map view -->
        <v-btn
          v-if="activeView === 'map'"
          small
          depressed
          color="primary"
          class="font-weight-bold ml-2 my-1"
          @click="toggleFullscreen"
        >
          <v-icon left small>{{ isFullscreen ? 'mdi-fullscreen-exit' : 'mdi-fullscreen' }}</v-icon>
          {{ isFullscreen ? 'Sulje koko näyttö' : 'Avaa koko ruudulle' }}
        </v-btn>
      </div>
    </v-card-title>

    <v-divider />

    <v-card-text class="pa-3">

      <!-- ======================================================== -->
      <!-- VIEW 1: CANONICAL TOKEVA TACTICAL DIAGRAM (SVG)           -->
      <!-- Matches Tokeva's canonical SVG geometry exactly          -->
      <!-- ======================================================== -->
      <div
        v-show="activeView === 'diagram'"
        class="diagram-container text-center py-2"
      >
        <div class="svg-wrapper">
          <svg
            viewBox="0 0 460 460"
            class="danger-zone-svg"
            xmlns="http://www.w3.org/2000/svg"
          >
            <!-- 1. Outer green circle (Pelastustoiminta-alue base) -->
            <circle
              cx="230"
              cy="230"
              r="200"
              fill="#75dd04"
              stroke="#5bb503"
              stroke-width="2"
            />

            <!-- 2. Yellow 240° downwind sector (Vaara-alue: sweeps from top-left, right, to bottom-left) -->
            <path
              d="M 135 65.5 A 190 190 0 1 1 135 394.5 L 230 230 Z"
              fill="#fef200"
              stroke="#e6d800"
              stroke-width="2.2"
            />

            <!-- 3. Decontamination / Wash station dashed box in upper green sector -->
            <g>
              <rect
                x="85.5"
                y="152.5"
                width="98.9"
                height="38.9"
                fill="#75dd04"
                stroke="#fef200"
                stroke-width="1.3"
                stroke-dasharray="3.5, 3.5"
                rx="3"
              />
              <text
                x="135"
                y="166"
                text-anchor="middle"
                font-size="10"
                fill="#000000"
                font-weight="bold"
              >
                huuhtelu- /
              </text>
              <text
                x="135"
                y="178"
                text-anchor="middle"
                font-size="10"
                fill="#000000"
                font-weight="bold"
              >
                puhdistus-
              </text>
              <text
                x="135"
                y="189"
                text-anchor="middle"
                font-size="10"
                fill="#000000"
                font-weight="bold"
              >
                paikka
              </text>
            </g>

            <!-- 4. Inner dashed ring between red circle and yellow zone -->
            <circle
              cx="230"
              cy="230"
              r="90"
              fill="#fef200"
              stroke="none"
            />

            <!-- 5. Red central circle: Välittömän vaaran alue (PELASTA) -->
            <circle
              cx="230"
              cy="230"
              r="80"
              fill="#ad0014"
              stroke="#8a0010"
              stroke-width="2"
            />

            <!-- ================= LABELS ================= -->
            <!-- Green sector labels (Left) -->
            <text
              x="58"
              y="222"
              font-size="12"
              font-weight="bold"
              fill="#000000"
              letter-spacing="0.5"
            >
              <tspan x="58" y="222">PELASTUS-</tspan>
              <tspan x="58" y="237">TOIMINTA-</tspan>
              <tspan x="58" y="252">ALUE</tspan>
            </text>
            <text
              x="56"
              y="289"
              font-size="12"
              font-style="italic"
              fill="#000000"
            >
              RYHMITTYMINEN
            </text>

            <!-- Yellow zone labels -->
            <text
              x="250"
              y="98"
              font-size="14"
              font-style="italic"
              font-weight="bold"
              fill="#000000"
              letter-spacing="0.5"
            >
              SUOJAUDU
            </text>

            <text
              x="305"
              y="170"
              font-size="12"
              font-weight="bold"
              fill="#000000"
              letter-spacing="0.5"
            >
              VAARA-ALUE
            </text>

            <text
              x="232"
              y="358"
              font-size="13"
              font-style="italic"
              font-weight="bold"
              fill="#000000"
              letter-spacing="0.5"
            >
              EVAKUOI JA ERISTÄ
            </text>

            <!-- Red circle labels -->
            <text
              x="230"
              y="185"
              text-anchor="middle"
              font-size="12"
              font-weight="bold"
              fill="#ffffff"
              letter-spacing="0.5"
            >
              <tspan x="230" y="185">VÄLITTÖMÄN</tspan>
              <tspan x="230" y="200">VAARAN ALUE</tspan>
            </text>
            <text
              x="230"
              y="279"
              text-anchor="middle"
              font-size="13"
              font-style="italic"
              font-weight="bold"
              fill="#ffffff"
              letter-spacing="1"
            >
              PELASTA
            </text>

            <!-- ================= DIMENSION ARROWS (STARTING FROM CENTER x=230) ================= -->
            <!-- ARROW 1: Välitön vaara (Center to red circle edge) -->
            <!-- Center vertical reference tick at x=230 -->
            <path d="M 230 216 V 232" stroke="#ffffff" stroke-width="2" />
            <!-- Line from center x=230 to 304 -->
            <path d="M 230 224 H 304" stroke="#ffffff" stroke-width="2" />
            <!-- White Arrowhead at edge of red circle (x=308) -->
            <polygon points="304,220 310,224 304,228" fill="#ffffff" />
            <!-- Red box with white meter text -->
            <rect x="238" y="212" width="62" height="22" rx="3" fill="#AE0015" />
            <text
              x="269"
              y="228"
              font-size="12.5"
              font-weight="bold"
              fill="#ffffff"
              text-anchor="middle"
            >
              {{ valitonVaaraMetrit }}
            </text>

            <!-- ARROW 2: Vaara-alue / Riski (Center to outer yellow perimeter x=418) -->
            <!-- Center vertical reference tick at x=230 -->
            <path d="M 230 238 V 254" stroke="#000000" stroke-width="2" />
            <!-- Line from center x=230 all the way to 414 -->
            <path d="M 230 246 H 414" stroke="#000000" stroke-width="2.2" />
            <!-- Black Arrowhead at outer yellow perimeter (x=418) -->
            <polygon points="414,241 420,246 414,251" fill="#000000" />
            <!-- Yellow box with black text -->
            <rect x="330" y="234" width="68" height="22" rx="3" fill="#fef200" />
            <text
              x="364"
              y="250"
              font-size="13"
              font-weight="bold"
              fill="#000000"
              text-anchor="middle"
            >
              {{ suojaAlueMetrit }}
            </text>

            <!-- TUULEN SUUNTA label directly below arrow 2 (hidden if T1 / no wind dependence) -->
            <text
              v-if="tuulenSuuntaText"
              x="325"
              y="270"
              font-size="12"
              font-style="italic"
              font-weight="bold"
              fill="#000000"
            >
              {{ tuulenSuuntaText }}
            </text>
          </svg>
        </div>
      </div>

      <!-- ======================================================== -->
      <!-- VIEW 2: TILANNEKARTTA (LIVE LEAFLET MAP)                 -->
      <!-- ======================================================== -->
      <div
        v-show="activeView === 'map'"
        :class="['map-view-container', { 'map-fullscreen-overlay': isFullscreen }]"
      >
        <!-- Floating Top Bar (Fullscreen Modal Only) -->
        <div v-if="isFullscreen" class="fullscreen-floating-top">
          <div class="d-flex align-center flex-wrap">
            <v-btn
              color="error"
              dark
              depressed
              class="fullscreen-close-btn font-weight-bold rounded-pill px-3 mr-2"
              @click="toggleFullscreen"
            >
              <v-icon left small>mdi-close</v-icon>
              Sulje
            </v-btn>

            <div class="fullscreen-tactical-pill elevation-3 rounded-pill px-3 py-1 d-flex align-center">
              <span class="error--text font-weight-black mr-2 d-flex align-center caption">
                <v-icon x-small color="error" class="mr-1">mdi-alert-circle</v-icon>
                0–{{ currentRadius.inner }} m
              </span>
              <span class="grey--text text--darken-1 mr-2">|</span>
              <span class="amber--text text--darken-4 font-weight-black mr-2 d-flex align-center caption">
                <v-icon x-small color="amber darken-4" class="mr-1">mdi-shield-alert</v-icon>
                {{ currentRadius.hasOuter ? currentRadius.outer + ' m' : 'RISKI' }}
              </span>
              <v-chip
                v-if="chemicalName"
                x-small
                color="orange darken-4"
                dark
                class="font-weight-bold d-none d-sm-inline-flex ml-1"
              >
                {{ chemicalName }}
              </v-chip>
              <v-chip
                v-if="isCustomDimensions"
                x-small
                color="warning darken-2"
                dark
                class="font-weight-bold ml-1"
              >
                PTJ
              </v-chip>
            </div>
          </div>

          <!-- Top-Right Floating Controls (GPS & Search Toggle) -->
          <div class="d-flex align-center">
            <v-btn
              fab
              x-small
              color="white"
              light
              class="elevation-3 mr-1"
              title="Paikanna oma sijainti (GPS)"
              :loading="loadingLocation"
              @click="useCurrentLocation"
            >
              <v-icon small color="secondary">mdi-crosshairs-gps</v-icon>
            </v-btn>

            <v-btn
              fab
              x-small
              :color="showFullscreenSearch ? 'primary' : 'white'"
              :dark="showFullscreenSearch"
              :light="!showFullscreenSearch"
              class="elevation-3"
              title="Hae osoitetta"
              @click="showFullscreenSearch = !showFullscreenSearch"
            >
              <v-icon small :color="showFullscreenSearch ? 'white' : 'primary'">
                {{ showFullscreenSearch ? 'mdi-close' : 'mdi-magnify' }}
              </v-icon>
            </v-btn>
          </div>
        </div>

        <!-- Fullscreen Search Overlay Dropdown -->
        <transition name="slide-y-transition">
          <div
            v-if="isFullscreen && showFullscreenSearch"
            class="fullscreen-search-overlay elevation-6 rounded-lg pa-2"
          >
            <v-text-field
              v-model="searchQuery"
              placeholder="Kirjoita osoite tai paikannimi..."
              dense
              solo
              flat
              outlined
              hide-details
              prepend-inner-icon="mdi-magnify"
              clearable
              autofocus
              @keydown.enter="searchAddressFullscreen"
            >
              <template v-slot:append>
                <v-btn
                  small
                  color="primary"
                  depressed
                  class="font-weight-bold"
                  :loading="loadingGeocode"
                  @click="searchAddressFullscreen"
                >
                  Hae
                </v-btn>
              </template>
            </v-text-field>
          </div>
        </transition>

        <!-- Fullscreen Floating Bottom Dock (Mobile & Desktop thumb friendly) -->
        <div v-if="isFullscreen" class="fullscreen-floating-bottom">
          <div class="fullscreen-bottom-dock elevation-6 rounded-pill pa-1 d-flex align-center">
            <v-btn-toggle
              v-model="leakType"
              mandatory
              dense
              class="leak-toggle-group mr-1"
            >
              <v-btn
                value="small"
                small
                class="font-weight-bold leak-btn leak-btn-small"
                :class="{ 'leak-btn-active': leakType === 'small' }"
              >
                Pieni
              </v-btn>
              <v-btn
                value="large"
                small
                class="font-weight-bold leak-btn leak-btn-large"
                :class="{ 'leak-btn-active': leakType === 'large' }"
              >
                Suuri
              </v-btn>
            </v-btn-toggle>

            <v-btn
              small
              rounded
              depressed
              :color="isCustomDimensions ? 'warning darken-2' : 'white'"
              :class="isCustomDimensions ? 'white--text font-weight-black' : 'primary--text font-weight-bold'"
              class="elevation-1"
              @click="openCustomDimensionsDialog"
            >
              <v-icon left x-small>{{ isCustomDimensions ? 'mdi-pencil-ruler' : 'mdi-ruler-square' }}</v-icon>
              {{ isCustomDimensions ? 'PTJ' : 'Mitoitus' }}
            </v-btn>
          </div>
        </div>

        <!-- Active Alarm Banner (Inline mode only) -->
        <v-alert
          v-if="hasActiveAlarm && !isFullscreen"
          color="error"
          dark
          dense
          class="mb-2 d-flex align-center flex-wrap"
        >
          <div class="d-flex align-center flex-grow-1 mr-2">
            <v-icon left>mdi-alert-octagon</v-icon>
            <div>
              <strong>HÄLYTYS KÄYNNISSÄ:</strong> {{ activeAlarm.code }} -
              {{ activeAlarm.address }}
              <span
                v-if="activeAlarm.time"
                class="caption ml-1 font-weight-regular"
              >
                ({{ activeAlarm.time }})
              </span>
            </div>
          </div>
          <v-btn
            x-small
            color="white"
            light
            class="font-weight-bold my-1"
            @click="resetToAlarmAddress"
          >
            <v-icon left x-small>mdi-map-marker</v-icon>
            Kohdista hälytysosoitteeseen
          </v-btn>
        </v-alert>

        <!-- Address Search & Location Tools (Inline mode only) -->
        <v-card v-if="!isFullscreen" outlined class="pa-2 mb-2 grey lighten-4 address-tools-card">
          <v-row dense align="center">
            <v-col cols="12" sm="7" md="8">
              <v-text-field
                v-model="searchQuery"
                placeholder="Kirjoita onnettomuusosoite tai paikannimi..."
                dense
                solo
                flat
                outlined
                hide-details
                prepend-inner-icon="mdi-magnify"
                clearable
                @keydown.enter="searchAddress"
              />
            </v-col>
            <v-col cols="6" sm="2" class="d-flex">
              <v-btn
                color="primary"
                block
                dense
                class="font-weight-bold"
                :loading="loadingGeocode"
                @click="searchAddress"
              >
                Hae
              </v-btn>
            </v-col>
            <v-col cols="6" sm="3" md="2" class="d-flex">
              <v-btn
                outlined
                color="secondary"
                block
                dense
                :loading="loadingLocation"
                @click="useCurrentLocation"
              >
                <v-icon left small>mdi-crosshairs-gps</v-icon>
                GPS
              </v-btn>
            </v-col>
          </v-row>
          <div
            class="caption grey--text text--darken-2 mt-1 d-flex align-center"
          >
            <v-icon x-small color="grey" class="mr-1">mdi-cursor-pointer</v-icon>
            Voit myös klikata karttaa tai raahata punaista onnettomuusmerkkiä siirtääksesi paikkaa.
          </div>
        </v-card>

        <!-- TACTICAL DISTANCES SUMMARY TEXT ABOVE THE MAP (Inline mode only) -->
        <div
          v-if="!isFullscreen"
          class="tactical-distance-bar pa-2 mb-2 rounded grey lighten-4 d-flex align-center justify-space-between flex-wrap elevation-1"
        >
          <div class="d-flex align-center flex-wrap">
            <div class="mr-4 my-1">
              <span class="caption grey--text text--darken-2 d-block font-weight-bold">
                VÄLITTÖMÄN VAARAN ALUE:
              </span>
              <span class="text-subtitle-2 font-weight-black error--text d-flex align-center">
                <v-icon small color="error" class="mr-1">mdi-alert-circle</v-icon>
                0 – {{ currentRadius.inner }} m
              </span>
              <span class="caption grey--text text--darken-1">Vain hengenpelastus (PELASTA)</span>
            </div>

            <div class="mr-4 my-1">
              <span class="caption grey--text text--darken-2 d-block font-weight-bold">
                VAARA-ALUE (Tuulen alapuoli):
              </span>
              <span class="text-subtitle-2 font-weight-black amber--text text--darken-4 d-flex align-center">
                <v-icon small color="amber darken-4" class="mr-1">mdi-shield-alert</v-icon>
                {{ currentRadius.hasOuter ? `${currentRadius.outer} m` : 'Mitoitetaan tilanteen mukaan (RISKI)' }}
              </span>
              <span class="caption grey--text text--darken-1">Suojaudu / Eristä</span>
            </div>

            <div class="my-1 mr-3">
              <span class="caption grey--text text--darken-2 d-block font-weight-bold">
                PELASTUSTOIMINTA-ALUE:
              </span>
              <span class="text-subtitle-2 font-weight-black green--text text--darken-3 d-flex align-center">
                <v-icon small color="green darken-3" class="mr-1">mdi-shield-check</v-icon>
                Tuulen yläpuolella (vastatuuleen) &gt; {{ currentRadius.inner }} m
              </span>
              <span class="caption grey--text text--darken-1">Ryhmittyminen & huuhtelupaikka</span>
            </div>
          </div>

          <div class="d-flex align-center flex-wrap my-1">
            <v-chip
              v-if="isCustomDimensions"
              small
              color="warning darken-2"
              dark
              class="font-weight-bold mr-2 my-1"
            >
              <v-icon left x-small>mdi-pencil-ruler</v-icon>
              PTJ:n mitoitus
            </v-chip>
            <v-btn
              v-if="isCustomDimensions"
              x-small
              text
              color="error"
              class="font-weight-bold mr-2 my-1"
              @click="resetToTokevaDimensions"
            >
              Palauta TOKEVA
            </v-btn>
            <v-btn
              small
              outlined
              color="primary"
              class="font-weight-bold my-1"
              :loading="loadingWeather"
              @click="fetchWeather(accidentCoords[0], accidentCoords[1])"
            >
              <v-icon left small>mdi-refresh</v-icon>
              Päivitä sää
            </v-btn>
          </div>
        </div>

        <!-- LEAFLET MAP WRAPPER -->
        <div
          class="map-wrapper rounded elevation-1 overflow-hidden"
          :style="isFullscreen ? 'width: 100%; height: 100%; position: absolute; inset: 0;' : ''"
        >
          <!-- Inline map fullscreen trigger button -->
          <v-btn
            v-if="!isFullscreen"
            x-small
            color="white"
            light
            class="map-floating-fullscreen-btn font-weight-bold elevation-3"
            @click="toggleFullscreen"
          >
            <v-icon left x-small color="primary">mdi-fullscreen</v-icon>
            Koko ruutu
          </v-btn>

          <!-- Floating Confirmation Banner (Higher on the page, at top of map) -->
          <transition name="slide-y-transition">
            <div
              v-if="showMoveConfirm && pendingAccidentCoords"
              class="map-confirm-move-banner elevation-6 rounded-lg pa-3"
            >
              <div class="d-flex align-center justify-space-between mb-2">
                <div class="d-flex align-center">
                  <v-icon color="warning" class="mr-2">mdi-map-marker-alert</v-icon>
                  <div>
                    <div class="font-weight-black body-2 warning--text text--darken-3">
                      Siirretäänkö onnettomuuspaikka?
                    </div>
                    <div class="caption grey--text text--darken-2" style="font-size: 11px !important;">
                      Klikkasit karttaa. Vahvista sijainnin siirto:
                      <span class="font-weight-bold black--text">
                        {{ pendingAccidentCoords[0].toFixed(5) }}, {{ pendingAccidentCoords[1].toFixed(5) }}
                      </span>
                    </div>
                  </div>
                </div>
                <v-btn icon x-small class="ml-2 grey--text" @click="cancelMoveAccident">
                  <v-icon small>mdi-close</v-icon>
                </v-btn>
              </div>
              <div class="d-flex align-center justify-end">
                <v-btn
                  small
                  text
                  color="grey darken-2"
                  class="mr-2 font-weight-medium"
                  @click="cancelMoveAccident"
                >
                  <v-icon small left>mdi-cancel</v-icon>
                  Peruuta
                </v-btn>
                <v-btn
                  small
                  color="error"
                  elevation="2"
                  class="font-weight-bold"
                  @click="confirmMoveAccident"
                >
                  <v-icon small left>mdi-check-bold</v-icon>
                  Kyllä, siirrä tähän
                </v-btn>
              </div>
            </div>
          </transition>

          <!-- Floating tactical wind compass widget in map top-right corner -->
          <div
            :class="['map-floating-wind-indicator elevation-3 rounded-lg px-2 py-1 d-flex align-center', { 'is-fullscreen-wind': isFullscreen }]"
            :title="windDirection !== null ? `Tuuli: ${windSpeed} m/s, suunta: ${windDirection}° (${getCompassName(windDirection)}) → ${(windDirection + 180) % 360}° (${getCompassName((windDirection + 180) % 360)})` : 'Haetaan tuulitietoja...'"
          >
            <div
              class="wind-compass-dial mr-2"
              :style="{ transform: `rotate(${downwindBearing}deg)` }"
            >
              <v-icon color="blue darken-3" small>mdi-arrow-up-bold-circle</v-icon>
            </div>
            <div class="d-flex flex-column" style="line-height: 1.15;">
              <div class="d-flex align-center">
                <span class="wind-label-tiny grey--text text--darken-2 font-weight-bold mr-1">TUULI</span>
                <span class="font-weight-black blue--text text--darken-4 wind-speed-text">
                  {{ windSpeed !== null ? `${windSpeed} m/s` : '—' }}
                </span>
              </div>
              <span
                v-if="windDirection !== null"
                class="caption grey--text text--darken-2 font-weight-bold"
                style="font-size: 10px !important; letter-spacing: 0.2px;"
              >
                {{ getCompassName(windDirection) }} → {{ getCompassName(downwindBearing) }}
              </span>
            </div>
          </div>

          <l-map
            ref="dangerZoneMap"
            :zoom="mapZoom"
            :center="accidentCoords"
            style="height: 100%; width: 100%"
            @click="onMapClick"
          >
            <l-tile-layer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution="&copy; OpenStreetMap contributors"
            />

            <!-- 0. Temporary Pending Accident Marker (Preview before confirmation) -->
            <l-marker
              v-if="pendingAccidentCoords"
              :lat-lng="pendingAccidentCoords"
              :icon="pendingMarkerIcon"
            >
              <l-tooltip permanent direction="top">
                Uusi ehdotettu sijainti (vahvista ylhäältä)
              </l-tooltip>
            </l-marker>

            <!-- 1. Outer full circular perimeter (Yellow dashed circle at currentRadius.outer) -->
            <l-circle
              :lat-lng="accidentCoords"
              :radius="currentRadius.outer"
              color="#f57f17"
              fill-color="#ffeb3b"
              :fill-opacity="0.12"
              :weight="2"
              dash-array="6, 6"
            >
              <l-tooltip>
                Vaara-alueen peruseristys: {{ currentRadius.outer }} m
              </l-tooltip>
            </l-circle>

            <!-- 2a. Upwind Pelastustoiminta-alue (Green 120° Sector matching Tokeva SVG green area) -->
            <l-polygon
              v-if="upwindSectorPoints.length > 0"
              :lat-lngs="upwindSectorPoints"
              color="#388e3c"
              fill-color="#7cb342"
              :fill-opacity="0.32"
              :weight="2"
            >
              <l-popup>
                <div class="pa-1">
                  <div class="font-weight-bold green--text text--darken-3">
                    PELASTUSTOIMINTA-ALUE (Tuulen yläpuoli, 120°)
                  </div>
                  <div>Turvallinen lähestymissuunta ja kalustoryhmittyminen tuulen yläpuolelta.</div>
                  <div class="caption font-weight-bold mt-1">
                    Säde: {{ currentRadius.outer }} metriä
                  </div>
                  <div class="caption">
                    Toimenpiteet: <strong>RYHMITTYMINEN / HUUHTELUPAIKKA</strong>
                  </div>
                </div>
              </l-popup>
              <l-tooltip>
                Pelastustoiminta-alue (Tuulen yläpuolella, 120°)
              </l-tooltip>
            </l-polygon>

            <!-- 2b. Downwind Hazard Zone (Yellow 240° Sector matching Tokeva SVG yellow area) -->
            <l-polygon
              v-if="downwindSectorPoints.length > 0"
              :lat-lngs="downwindSectorPoints"
              color="#f57f17"
              fill-color="#fef200"
              :fill-opacity="0.45"
              :weight="2.5"
            >
              <l-popup>
                <div class="pa-1">
                  <div class="font-weight-bold amber--text text--darken-4">
                    VAARA-ALUE (Tuulen alapuoli ja sivustat, 240°)
                  </div>
                  <div>Kaasut, höyryt ja leviäminen tähän 240° taktiseen sektoriin!</div>
                  <div class="caption font-weight-bold mt-1">
                    Säde: {{ currentRadius.outer }} metriä
                  </div>
                  <div class="caption">
                    Toimenpiteet: <strong>SUOJAUDU / EVAKUOI JA ERISTÄ</strong>
                  </div>
                </div>
              </l-popup>
              <l-tooltip>
                Vaara-alue: {{ currentRadius.outer }} m (Suojaudu / Eristä)
              </l-tooltip>
            </l-polygon>

            <!-- 3. Red Inner Circle: Välittömän vaaran alue (0 - currentRadius.inner) -->
            <l-circle
              :lat-lng="accidentCoords"
              :radius="currentRadius.inner"
              color="#b71c1c"
              fill-color="#d32f2f"
              :fill-opacity="0.45"
              :weight="3"
            >
              <l-popup>
                <div class="pa-1">
                  <div class="font-weight-bold error--text">
                    VÄLITTÖMÄN VAARAN ALUE
                  </div>
                  <div>
                    Säde: <strong>0 – {{ currentRadius.inner }} metriä</strong>
                  </div>
                  <div class="caption mt-1">
                    Toiminta: <strong>Vain hengenpelastus (PELASTA)</strong>
                  </div>
                  <div class="caption">Korkein kemikaalisuojaus vaaditaan.</div>
                </div>
              </l-popup>
              <l-tooltip>
                Välittömän vaaran alue ({{ currentRadius.inner }} m)
              </l-tooltip>
            </l-circle>

            <!-- 4. WIND DIRECTION VECTOR ON MAP (Blue dashed line with arrow tip) -->
            <l-polyline
              v-if="windVector.length === 2"
              :lat-lngs="windVector"
              color="#0288d1"
              :weight="4"
              dash-array="7, 6"
            />

            <!-- Wind Direction Arrow Marker at the tip of the vector -->
            <l-marker
              v-if="windVector.length === 2"
              :lat-lng="windVector[1]"
              :icon="windArrowIcon"
            >
              <l-tooltip permanent direction="bottom">
                Tuuli: {{ windSpeed !== null ? `${windSpeed} m/s` : '' }} (kohti {{ getCompassName(downwindBearing) }})
              </l-tooltip>
            </l-marker>

            <!-- 5. Safe Approach Corridor Line (Green Upwind Vector) -->
            <l-polyline
              v-if="approachVector.length === 2"
              :lat-lngs="approachVector"
              color="#2e7d32"
              :weight="5"
            />

            <!-- 5b. Safe Approach Arrow Marker at the tip of the vector (Pointing towards incident) -->
            <l-marker
              v-if="approachVector.length === 2"
              :lat-lng="approachVector[1]"
              :icon="approachArrowIcon"
            >
              <l-tooltip direction="top">
                Suositeltava lähestymissuunta kohti kohdetta ({{ getCompassName((windDirection + 180) % 360) }})
              </l-tooltip>
            </l-marker>

            <!-- 6. Safe Approach Marker (Upwind Corridor Indicator, positioned outside line) -->
            <l-marker
              v-if="approachVector.length === 2"
              :lat-lng="approachVector[0]"
              :icon="approachIcon"
            >
              <l-popup>
                <div class="pa-1">
                  <div class="font-weight-bold green--text text--darken-3">
                    SUOSITELTAVA LÄHESTYMISSUUNTA
                  </div>
                  <div>
                    Lähesty onnettomuuspaikkaa aina
                    <strong>tuulen yläpuolelta (vastatuuleen)</strong>!
                  </div>
                  <div class="caption mt-1">
                    Perusta kaluston ryhmittyminen ja huuhtelupaikka tähän sektoriin.
                  </div>
                </div>
              </l-popup>
              <l-tooltip direction="top">
                Suositeltava lähestymissuunta (Tuulen yläpuolelta)
              </l-tooltip>
            </l-marker>

            <!-- 7. Downwind Danger Warning Marker (Only shown when downwind dispersion exists) -->
            <l-marker
              v-if="downwindWarningPos && currentRadius.hasOuter"
              :lat-lng="downwindWarningPos"
              :icon="downwindWarningIcon"
            >
              <l-popup>
                <div class="pa-1">
                  <div class="font-weight-bold red--text">
                    VAARALLINEN LÄHESTYMISSUUNTA!
                  </div>
                  <div>
                    Älä lähesty tuulen alapuolelta! Myrkylliset tai syttyvät höyryt leviävät tänne.
                  </div>
                </div>
              </l-popup>
              <l-tooltip direction="bottom">
                Tuulen alapuoli (Vaarallinen!)
              </l-tooltip>
            </l-marker>

            <!-- 8. Accident Center Marker (Draggable) -->
            <l-marker
              ref="accidentMarker"
              :lat-lng="accidentCoords"
              :draggable="true"
              :icon="accidentIcon"
              @dragend="onMarkerDragEnd"
            >
              <l-popup>
                <div class="pa-1">
                  <div class="font-weight-bold text-subtitle-2 error--text">
                    {{ chemicalName || "Kemikaalionnettomuus" }}
                  </div>
                  <div v-if="unCode" class="caption font-weight-bold">
                    YK-numero: {{ unCode }}
                  </div>
                  <div class="caption mt-1">Sijainti: {{ accidentLabel }}</div>
                  <div class="caption">
                    Raahaa merkkiä siirtääksesi kohdetta.
                  </div>
                </div>
              </l-popup>
              <l-tooltip>Onnettomuuspaikka (Raahaa siirtääksesi)</l-tooltip>
            </l-marker>
          </l-map>
        </div>
      </div>

      <!-- ======================================================== -->
      <!-- TACTICAL INSTRUCTION CARDS (Action rules for each zone)  -->
      <!-- ======================================================== -->
      <v-row dense class="mt-4">
        <!-- Red Zone Rules -->
        <v-col cols="12" md="4">
          <v-sheet outlined rounded class="pa-3 fill-height border-red-zone">
            <div
              class="d-flex align-center font-weight-bold text-subtitle-2 error--text mb-2"
            >
              <v-icon small left color="error">mdi-alert-circle</v-icon>
              Välittömän vaaran alue (0 – {{ currentRadius.inner }} m)
            </div>
            <ul class="pl-4 text-body-2 mb-0" style="line-height: 1.55">
              <li class="mb-1">
                <strong>PELASTA:</strong> Toiminta rajataan vain välittömään
                ihmishenkien pelastamiseen.
              </li>
              <li class="mb-1">
                <strong>Suojaustaso:</strong> Vaatii täyden kemikaalisuojauksen
                ja paineilmalaitteen.
              </li>
              <li class="mb-1">
                Ei ylimääräistä henkilöstöä eikä ajoneuvoja tälle alueelle.
              </li>
            </ul>
          </v-sheet>
        </v-col>

        <!-- Yellow Zone Rules -->
        <v-col cols="12" md="4">
          <v-sheet outlined rounded class="pa-3 fill-height border-yellow-zone">
            <div
              class="d-flex align-center font-weight-bold text-subtitle-2 amber--text text--darken-4 mb-2"
            >
              <v-icon small left color="amber darken-4">mdi-shield-alert</v-icon>
              Vaara-alue ({{ currentRadius.hasOuter ? currentRadius.outer + ' m' : 'Mitoitetaan tilanteen mukaan' }})
            </div>
            <ul class="pl-4 text-body-2 mb-0" style="line-height: 1.55">
              <li class="mb-1">
                <strong>EVAKUOI JA ERISTÄ:</strong> Eristä vaara-alue ja sulje
                liikenne alueelle.
              </li>
              <li class="mb-1">
                <strong>SUOJAUDU:</strong> Väestön suojelu sisätiloihin, ilmanvaihdon
                sulkeminen ja ovien tiivistäminen.
              </li>
              <li class="mb-1">
                Tuulen alapuolen valvonta ja mittaukset leviämisen toteamiseksi.
              </li>
            </ul>
          </v-sheet>
        </v-col>

        <!-- Green Zone Rules -->
        <v-col cols="12" md="4">
          <v-sheet outlined rounded class="pa-3 fill-height border-green-zone">
            <div
              class="d-flex align-center font-weight-bold text-subtitle-2 green--text text--darken-3 mb-2"
            >
              <v-icon small left color="green darken-3">mdi-shield-check</v-icon>
              Pelastustoiminta-alue (Tuulen yläpuolella)
            </div>
            <ul class="pl-4 text-body-2 mb-0" style="line-height: 1.55">
              <li class="mb-1">
                <strong>Lähestyminen aina tuulen yläpuolelta:</strong>
                Ajoneuvojen ja kaluston ryhmittyminen.
              </li>
              <li class="mb-1">
                <strong>Huuhtelupaikka:</strong> Perustetaan tuulen yläpuolelle
                välittömän vaaran alueen rajalle ennen toiminnan aloitusta.
              </li>
              <li class="mb-1">
                Johtopaikka ja huolto turvallisella etäisyydellä vastatuulessa.
              </li>
            </ul>
          </v-sheet>
        </v-col>
      </v-row>
    </v-card-text>

    <!-- CUSTOM DIMENSIONS EDIT DIALOG (PTJ:n mukautettu mitoitus) -->
    <v-dialog
      v-model="showDimensionsDialog"
      max-width="500"
      persistent
      content-class="dimensions-custom-dialog"
    >
      <v-card class="rounded-lg">
        <v-card-title class="py-3 px-4 d-flex align-center justify-space-between orange darken-4 white--text">
          <div class="d-flex align-center">
            <v-icon left color="white">mdi-shield-edit</v-icon>
            <span class="text-subtitle-1 font-weight-bold">
              Muokkaa vaara-alueen etäisyyksiä
            </span>
          </div>
          <v-btn icon small dark @click="showDimensionsDialog = false">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-4 px-4 pb-2">
          <v-alert
            dense
            outlined
            color="info"
            class="caption mb-4"
          >
            <div class="d-flex align-center">
              <v-icon small color="info" class="mr-2">mdi-information</v-icon>
              <div>
                Pelastustoiminnan johtaja (PTJ) voi tilannearvion perusteella määrittää virallisia TOKEVA-etäisyyksiä laajemmat turva-alueet.
              </div>
            </div>
            <div class="mt-2 font-weight-bold grey--text text--darken-3">
              TOKEVA-suositus ({{ leakType === 'small' ? 'Pieni vuoto' : 'Suuri vuoto' }}):
              <span class="error--text font-weight-black">{{ tokevaDefaultRadius.inner }} m</span> (välitön) /
              <span class="amber--text text--darken-4 font-weight-black">{{ tokevaDefaultRadius.outer }} m</span> (vaara-alue)
            </div>
          </v-alert>

          <!-- Input 1: Välittömän vaaran alue -->
          <div class="mb-4">
            <div class="text-subtitle-2 font-weight-bold error--text d-flex align-center mb-1">
              <v-icon small color="error" class="mr-1">mdi-alert-circle</v-icon>
              Välittömän vaaran alue (0 – X metriä):
            </div>
            <v-text-field
              v-model.number="editInner"
              type="number"
              min="5"
              step="10"
              suffix="metriä"
              outlined
              dense
              hide-details="auto"
              class="font-weight-bold"
              prepend-inner-icon="mdi-radius"
            />
            <div class="caption grey--text text--darken-1 mt-1 mb-1">
              Punainen sisäalue (vain hengenpelastus, korkein kemikaalisuojaus).
            </div>
            <!-- Quick preset chips -->
            <div class="d-flex align-center flex-wrap mt-1">
              <span class="caption grey--text mr-2">Pikavalinnat:</span>
              <v-chip
                v-for="val in [25, 50, 100, 200, 400]"
                :key="'inner-' + val"
                x-small
                outlined
                color="error"
                class="mr-1 mb-1 font-weight-bold"
                :class="{ 'white--text error': editInner === val }"
                @click="editInner = val"
              >
                {{ val }} m
              </v-chip>
            </div>
          </div>

          <v-divider class="my-3" />

          <!-- Input 2: Vaara-alue (tuulen alapuoli) -->
          <div class="mb-3">
            <div class="text-subtitle-2 font-weight-bold amber--text text--darken-4 d-flex align-center mb-1">
              <v-icon small color="amber darken-4" class="mr-1">mdi-shield-alert</v-icon>
              Vaara-alue / tuulen alapuolen eristys (metriä):
            </div>
            <v-text-field
              v-model.number="editOuter"
              type="number"
              :min="editInner || 10"
              step="25"
              suffix="metriä"
              outlined
              dense
              hide-details="auto"
              class="font-weight-bold"
              prepend-inner-icon="mdi-weather-windy"
            />
            <div class="caption grey--text text--darken-1 mt-1 mb-1">
              Keltainen 240° sektori (tuulen alapuoli ja sivustat: suojaudu, evakuoi, eristä).
            </div>
            <!-- Quick preset chips -->
            <div class="d-flex align-center flex-wrap mt-1">
              <span class="caption grey--text mr-2">Pikavalinnat:</span>
              <v-chip
                v-for="val in [50, 100, 200, 400, 600, 1000]"
                :key="'outer-' + val"
                x-small
                outlined
                color="amber darken-4"
                class="mr-1 mb-1 font-weight-bold"
                :class="{ 'white--text amber darken-4': editOuter === val }"
                @click="editOuter = val"
              >
                {{ val }} m
              </v-chip>
            </div>
          </div>
        </v-card-text>

        <v-divider />

        <v-card-actions class="pa-3 d-flex justify-space-between flex-wrap">
          <v-btn
            text
            small
            color="error"
            class="font-weight-bold"
            @click="resetToTokevaDimensions"
          >
            <v-icon left small>mdi-restore</v-icon>
            Palauta TOKEVA-oletus
          </v-btn>

          <div>
            <v-btn
              text
              small
              class="mr-2"
              @click="showDimensionsDialog = false"
            >
              Peruuta
            </v-btn>
            <v-btn
              color="primary"
              small
              elevation="2"
              class="font-weight-bold"
              @click="applyCustomDimensions"
            >
              <v-icon left small>mdi-check</v-icon>
              Käytä näitä etäisyyksiä
            </v-btn>
          </div>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import {
  LMap,
  LTileLayer,
  LMarker,
  LPolyline,
  LCircle,
  LPolygon,
  LTooltip,
  LPopup,
} from "vue2-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import dayjs from "dayjs";
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
} from "firebase/firestore";
import { firestore as db } from "@/plugins/firebase.app";

// Fix standard Leaflet default icon paths
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const ACCIDENT_LOCATION_STORAGE_KEY = "tokeva_accident_location";
const ACCIDENT_LOCATION_MAX_AGE_MS = 48 * 60 * 60 * 1000; // 48 hours

export default {
  name: "TokevaDangerZone",
  components: {
    LMap,
    LTileLayer,
    LMarker,
    LPolyline,
    LCircle,
    LPolygon,
    LTooltip,
    LPopup,
  },
  props: {
    smallZone: {
      type: String,
      default: "25 / 50",
    },
    largeZone: {
      type: String,
      default: "50 / 100",
    },
    chemicalName: {
      type: String,
      default: "",
    },
    unCode: {
      type: String,
      default: "",
    },
    tInstruction: {
      type: String,
      default: "",
    },
  },
  data: () => {
    let initialCoords = [61.5928, 21.8687]; // Default Noormarkku station coords
    let initialLabel = "Noormarkku";
    try {
      const raw = localStorage.getItem(ACCIDENT_LOCATION_STORAGE_KEY);
      if (raw) {
        const d = JSON.parse(raw);
        if (d && d.coords && Array.isArray(d.coords) && d.coords.length >= 2) {
          const age = Date.now() - (d.timestamp || 0);
          if (age <= ACCIDENT_LOCATION_MAX_AGE_MS) {
            initialCoords = [Number(d.coords[0]), Number(d.coords[1])];
            initialLabel = d.label || initialLabel;
          } else {
            localStorage.removeItem(ACCIDENT_LOCATION_STORAGE_KEY);
          }
        }
      }
    } catch (e) {
      console.warn("Failed reading saved accident coords from localStorage", e);
    }

    return {
      activeView: "diagram", // 'diagram' | 'map'
      leakType: "small", // 'small' | 'large'
      isFullscreen: false,
      showFullscreenSearch: false,

      // Map & Location State
      mapZoom: 15,
      accidentCoords: initialCoords,
      accidentLabel: initialLabel,
      originalAlarmCoords: null,
      searchQuery: "",
      loadingGeocode: false,
      loadingLocation: false,

      // Map Move Confirmation State (Prevents accidental moves while panning)
      showMoveConfirm: false,
      pendingAccidentCoords: null,

      // Custom Dimensions State (PTJ:n mukautettu mitoitus)
      isCustomDimensions: false,
      customInner: 25,
      customOuter: 50,
      showDimensionsDialog: false,
      editInner: 25,
      editOuter: 50,

      // Active Alarm State
      activeAlarm: null,

      // Live Weather State
      loadingWeather: false,
      windSpeed: null,
      windDirection: 225, // default degrees (SW)
      windGust: null,
      temperature: null,
      weatherFetchedAt: null,
      weatherLocationName: "",
    };
  },
  computed: {
    hasActiveAlarm() {
      return !!this.activeAlarm;
    },
    tokevaDefaultRadius() {
      const raw =
        this.leakType === "small"
          ? this.smallZone || "25 / 50"
          : this.largeZone || "50 / 100";
      return this.parseDimensions(raw);
    },
    currentRadius() {
      if (this.isCustomDimensions) {
        const inner = Math.max(5, parseInt(this.customInner, 10) || 25);
        const outer = Math.max(inner, parseInt(this.customOuter, 10) || inner);
        return {
          inner,
          outer,
          hasOuter: outer > inner,
        };
      }
      return this.tokevaDefaultRadius;
    },
    smallZoneLabel() {
      const p = this.parseDimensions(this.smallZone || "25 / 50");
      return p.hasOuter ? `${p.inner} / ${p.outer} m` : `${p.inner} m`;
    },
    largeZoneLabel() {
      const p = this.parseDimensions(this.largeZone || "50 / 100");
      return p.hasOuter ? `${p.inner} / ${p.outer} m` : `${p.inner} m`;
    },
    valitonVaaraMetrit() {
      return `${this.currentRadius.inner} m`;
    },
    suojaAlueMetrit() {
      if (!this.currentRadius.hasOuter && !this.isCustomDimensions) {
        return "RISKI";
      }
      return `${this.currentRadius.outer} m`;
    },
    tuulenSuuntaText() {
      if (this.isT1Instruction || !this.currentRadius.hasOuter) {
        return "";
      }
      return "TUULEN SUUNTA";
    },
    isT1Instruction() {
      const t = (this.tInstruction || "").toLowerCase();
      return t.startsWith("t1") || t === "t1";
    },
    downwindBearing() {
      return (this.windDirection + 180) % 360;
    },
    // Downwind hazard zone polygon on Leaflet map (240 degrees matching Tokeva SVG yellow area)
    downwindSectorPoints() {
      if (!this.accidentCoords || this.accidentCoords.length < 2) return [];
      const [lat, lng] = this.accidentCoords;
      const radius = this.currentRadius.outer;
      const downwind = this.downwindBearing;
      const points = [[lat, lng]];
      const halfSpread = 120; // 240-degree tactical zone matching Tokeva SVG yellow area!
      for (let a = downwind - halfSpread; a <= downwind + halfSpread; a += 4) {
        const bearing = (a + 360) % 360;
        points.push(this.destinationPoint(lat, lng, radius, bearing));
      }
      points.push([lat, lng]);
      return points;
    },
    // Safe upwind sector (Pelastustoiminta-alue: 120 degrees facing upwind, matching Tokeva SVG green area)
    upwindSectorPoints() {
      if (!this.accidentCoords || this.accidentCoords.length < 2) return [];
      const [lat, lng] = this.accidentCoords;
      const radius = this.currentRadius.outer;
      const upwind = this.windDirection;
      const points = [[lat, lng]];
      const halfSpread = 60; // 120-degree upwind sector matching Tokeva SVG green area
      for (let a = upwind - halfSpread; a <= upwind + halfSpread; a += 4) {
        const bearing = (a + 360) % 360;
        points.push(this.destinationPoint(lat, lng, radius, bearing));
      }
      points.push([lat, lng]);
      return points;
    },
    // Safe Approach Corridor vector (arriving from upwind, stays safely OUTSIDE the danger zone)
    approachVector() {
      if (!this.accidentCoords || this.accidentCoords.length < 2) return [];
      const [lat, lng] = this.accidentCoords;
      const maxRadius = Math.max(this.currentRadius.inner, this.currentRadius.outer);
      const startDist = maxRadius * 1.45 + 75;
      const endDist = maxRadius + 20;
      const pStart = this.destinationPoint(lat, lng, startDist, this.windDirection);
      const pEnd = this.destinationPoint(lat, lng, endDist, this.windDirection);
      return [pStart, pEnd];
    },
    // Wind vector polyline starting from accident location extending downwind
    windVector() {
      if (!this.accidentCoords || this.accidentCoords.length < 2) return [];
      const [lat, lng] = this.accidentCoords;
      const maxRadius = Math.max(this.currentRadius.inner, this.currentRadius.outer);
      const dist = Math.max(maxRadius * 0.95, 160);
      const pEnd = this.destinationPoint(lat, lng, dist, this.downwindBearing);
      return [[lat, lng], pEnd];
    },
    // Position of the downwind warning marker (placed well outside the danger perimeter)
    downwindWarningPos() {
      if (!this.accidentCoords || this.accidentCoords.length < 2) return null;
      const [lat, lng] = this.accidentCoords;
      const maxRadius = Math.max(this.currentRadius.inner, this.currentRadius.outer);
      return this.destinationPoint(
        lat,
        lng,
        maxRadius * 1.2 + 50,
        this.downwindBearing,
      );
    },
    accidentIcon() {
      return L.divIcon({
        className: "accident-map-icon",
        html: `
          <div class="accident-pulse-wrapper">
            <div class="accident-pulse-ring"></div>
            <div class="accident-marker-core">
              <span class="mdi mdi-alert-octagon"></span>
            </div>
          </div>`,
        iconSize: [36, 36],
        iconAnchor: [18, 18],
      });
    },
    pendingMarkerIcon() {
      return L.divIcon({
        className: "pending-map-icon",
        html: `
          <div class="pending-pulse-wrapper">
            <div class="pending-pulse-ring"></div>
            <div class="pending-marker-core">
              <span class="mdi mdi-map-marker-question"></span>
            </div>
          </div>`,
        iconSize: [36, 36],
        iconAnchor: [18, 18],
      });
    },
    approachArrowIcon() {
      const bearing = (this.windDirection + 180) % 360;
      return L.divIcon({
        className: "approach-direction-map-icon",
        html: `
          <div class="approach-arrow-wrapper">
            <div class="approach-arrow-circle" style="transform: rotate(${bearing}deg);">
              <span class="mdi mdi-arrow-up-bold"></span>
            </div>
          </div>`,
        iconSize: [26, 26],
        iconAnchor: [13, 13],
      });
    },
    approachIcon() {
      // Approach line starts at approachVector[0] and heads towards the incident along (windDirection + 180) % 360.
      // We position the badge at approachVector[0], dynamically shifted in the direction of windDirection (away from the line)
      // so it is mathematically impossible for the badge and the approach line to overlap at any angle or zoom level.
      const phi = (this.windDirection * Math.PI) / 180;
      const dx = Math.sin(phi);
      const dy = -Math.cos(phi);

      const W = 142;
      const H = 20;
      const hw = W / 2;
      const hh = H / 2;

      const absDx = Math.abs(dx);
      const absDy = Math.abs(dy);
      const distToEdgeX = absDx > 0.0001 ? hw / absDx : Infinity;
      const distToEdgeY = absDy > 0.0001 ? hh / absDy : Infinity;
      const edgeDist = Math.min(distToEdgeX, distToEdgeY);

      // Clean clearance gap in screen pixels from the start of the green polyline
      const gap = 10;
      const totalShift = edgeDist + gap;

      const centerOffsetX = totalShift * dx;
      const centerOffsetY = totalShift * dy;

      const anchorX = Math.round(hw - centerOffsetX);
      const anchorY = Math.round(hh - centerOffsetY);

      return L.divIcon({
        className: "approach-map-icon",
        html: `
          <div class="approach-marker-badge">
            <span class="mdi mdi-shield-check mr-1"></span>
            <span>Suositeltava lähestyminen</span>
          </div>`,
        iconSize: [W, H],
        iconAnchor: [anchorX, anchorY],
      });
    },
    downwindWarningIcon() {
      return L.divIcon({
        className: "downwind-map-icon",
        html: `
          <div class="downwind-warning-badge">
            <span class="mdi mdi-alert-circle mr-1"></span>
            <span>TUULEN ALAPUOLI</span>
          </div>`,
        iconSize: [124, 20],
        iconAnchor: [62, -8],
      });
    },
    windArrowIcon() {
      const bearing = this.downwindBearing;
      return L.divIcon({
        className: "wind-direction-map-icon",
        html: `
          <div class="wind-arrow-wrapper">
            <div class="wind-arrow-circle" style="transform: rotate(${bearing}deg);">
              <span class="mdi mdi-arrow-up-bold"></span>
            </div>
          </div>`,
        iconSize: [32, 32],
        iconAnchor: [16, 16],
      });
    },
  },
  watch: {
    leakType() {
      // Revert to official TOKEVA leak dimensions when user selects Pieni / Suuri vuoto
      this.isCustomDimensions = false;
    },
    activeView(val) {
      if (val === "map") {
        this.invalidateMapSize();
      }
    },
    currentRadius: {
      handler(newVal) {
        if (!newVal) return;
        const maxR = Math.max(newVal.inner, newVal.outer);
        if (maxR >= 1000) this.mapZoom = 13;
        else if (maxR >= 400) this.mapZoom = 14;
        else if (maxR >= 100) this.mapZoom = 15;
        else this.mapZoom = 16;
      },
      immediate: true,
    },
  },
  mounted() {
    this.checkActiveAlarm();
    window.addEventListener("keydown", this.onKeyDown);
  },
  beforeDestroy() {
    window.removeEventListener("keydown", this.onKeyDown);
    document.body.style.overflow = "";
  },
  methods: {
    parseDimensions(str) {
      if (!str) return { inner: 25, outer: 50, hasOuter: false };
      const parts = str.split("/").map((s) => parseInt(s.trim(), 10));
      const inner = !isNaN(parts[0]) ? parts[0] : 25;
      const hasOuter = parts.length > 1 && !isNaN(parts[1]);
      const outer = hasOuter ? Math.max(inner, parts[1]) : inner;
      return {
        inner,
        outer,
        hasOuter,
      };
    },
    onKeyDown(e) {
      if (e.key === "Escape" && this.isFullscreen) {
        this.toggleFullscreen();
      }
    },
    toggleFullscreen() {
      this.isFullscreen = !this.isFullscreen;
      if (this.isFullscreen) {
        document.body.style.overflow = "hidden";
      } else {
        document.body.style.overflow = "";
        this.showFullscreenSearch = false;
      }
      this.invalidateMapSize();
    },
    async searchAddressFullscreen() {
      await this.searchAddress();
      this.showFullscreenSearch = false;
    },
    invalidateMapSize() {
      this.$nextTick(() => {
        setTimeout(() => {
          if (this.$refs.dangerZoneMap && this.$refs.dangerZoneMap.mapObject) {
            this.$refs.dangerZoneMap.mapObject.invalidateSize();
          }
        }, 120);
        setTimeout(() => {
          if (this.$refs.dangerZoneMap && this.$refs.dangerZoneMap.mapObject) {
            this.$refs.dangerZoneMap.mapObject.invalidateSize();
          }
        }, 320);
      });
    },
    destinationPoint(lat, lng, distMeters, bearingDeg) {
      const R = 6378137;
      const d = distMeters / R;
      const brng = (bearingDeg * Math.PI) / 180;
      const lat1 = (lat * Math.PI) / 180;
      const lon1 = (lng * Math.PI) / 180;
      const lat2 = Math.asin(
        Math.sin(lat1) * Math.cos(d) +
          Math.cos(lat1) * Math.sin(d) * Math.cos(brng),
      );
      const lon2 =
        lon1 +
        Math.atan2(
          Math.sin(brng) * Math.sin(d) * Math.cos(lat1),
          Math.cos(d) - Math.sin(lat1) * Math.sin(lat2),
        );
      return [(lat2 * 180) / Math.PI, (lon2 * 180) / Math.PI];
    },
    getCompassName(deg) {
      if (deg === null || deg === undefined) return "";
      const val = Math.floor(deg / 22.5 + 0.5) % 16;
      const arr = [
        "P",
        "P-KO",
        "KO",
        "I-KO",
        "I",
        "I-KA",
        "KA",
        "E-KA",
        "E",
        "E-LO",
        "LO",
        "L-LO",
        "L",
        "L-LU",
        "LU",
        "P-LU",
      ];
      return arr[val];
    },
    saveAccidentLocation(coords, label) {
      if (!coords || !Array.isArray(coords) || coords.length < 2) return;
      try {
        const payload = {
          coords: [Number(coords[0]), Number(coords[1])],
          label:
            label ||
            `${Number(coords[0]).toFixed(5)}, ${Number(coords[1]).toFixed(5)}`,
          timestamp: Date.now(),
        };
        localStorage.setItem(
          ACCIDENT_LOCATION_STORAGE_KEY,
          JSON.stringify(payload)
        );
      } catch (e) {
        console.warn("Failed to save accident location to localStorage", e);
      }
    },
    loadSavedAccidentLocation() {
      try {
        const raw = localStorage.getItem(ACCIDENT_LOCATION_STORAGE_KEY);
        if (!raw) return null;
        const data = JSON.parse(raw);
        if (!data || !data.coords || data.coords.length < 2) return null;

        const age = Date.now() - (data.timestamp || 0);
        if (age > ACCIDENT_LOCATION_MAX_AGE_MS) {
          localStorage.removeItem(ACCIDENT_LOCATION_STORAGE_KEY);
          return null;
        }
        return data;
      } catch (e) {
        console.warn("Failed to load saved accident location", e);
        return null;
      }
    },
    async checkActiveAlarm() {
      try {
        const digivihotRef = collection(db, "digivihot");
        const q = query(
          digivihotRef,
          where("type", "==", "Hälytys"),
          where("deleted", "==", false),
          orderBy("date", "desc"),
          limit(3),
        );

        const querySnapshot = await getDocs(q);
        if (querySnapshot.empty) {
          this.activeAlarm = null;
          const savedLoc = this.loadSavedAccidentLocation();
          if (savedLoc) {
            this.accidentCoords = [...savedLoc.coords];
            this.accidentLabel = savedLoc.label;
          }
          this.fetchWeather(this.accidentCoords[0], this.accidentCoords[1]);
          return;
        }

        const alarms = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        alarms.sort((a, b) => {
          let dateA = a.date || "";
          if (a.time) {
            dateA += "T" + a.time;
            if (a.time.length === 5) dateA += ":00";
          }
          let dateB = b.date || "";
          if (b.time) {
            dateB += "T" + b.time;
            if (b.time.length === 5) dateB += ":00";
          }
          return dayjs(dateB).diff(dayjs(dateA));
        });

        const latest = alarms[0];
        let dateTimeStr = latest.date || "";
        if (latest.time) {
          dateTimeStr += "T" + latest.time;
          if (latest.time.length === 5) dateTimeStr += ":00";
        }

        const alarmTime = dayjs(dateTimeStr);
        const now = dayjs();
        const diffMins = now.diff(alarmTime, "minute");

        // Consider active if within 2 hours (120 min)
        if (diffMins < 120 && diffMins >= 0) {
          this.activeAlarm = latest;
          this.activeView = "map"; // Automatically open map view if alarm is active!
          
          const savedLoc = this.loadSavedAccidentLocation();
          const alarmTimestamp = alarmTime.valueOf();
          // If the user already saved a manual location AFTER this alarm's dispatch time:
          // Keep user's manual location choice across page reloads
          if (savedLoc && savedLoc.timestamp && savedLoc.timestamp > alarmTimestamp) {
            this.accidentCoords = [...savedLoc.coords];
            this.accidentLabel = savedLoc.label;
            this.fetchWeather(this.accidentCoords[0], this.accidentCoords[1]);
          } else if (latest.address) {
            this.geocodeAlarmAddress(latest.address);
          } else {
            this.fetchWeather(this.accidentCoords[0], this.accidentCoords[1]);
          }
        } else {
          this.activeAlarm = null;
          const savedLoc = this.loadSavedAccidentLocation();
          if (savedLoc) {
            this.accidentCoords = [...savedLoc.coords];
            this.accidentLabel = savedLoc.label;
          }
          this.fetchWeather(this.accidentCoords[0], this.accidentCoords[1]);
        }
      } catch (e) {
        console.error("Failed to check active alarm", e);
        const savedLoc = this.loadSavedAccidentLocation();
        if (savedLoc) {
          this.accidentCoords = [...savedLoc.coords];
          this.accidentLabel = savedLoc.label;
        }
        this.fetchWeather(this.accidentCoords[0], this.accidentCoords[1]);
      }
    },
    async geocodeAlarmAddress(addr) {
      if (!addr) return;
      this.loadingGeocode = true;
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(
            addr,
          )}`,
        );
        const data = await response.json();
        if (data && data.length > 0) {
          const lat = parseFloat(data[0].lat);
          const lng = parseFloat(data[0].lon);
          this.accidentCoords = [lat, lng];
          this.originalAlarmCoords = [lat, lng];
          this.accidentLabel = addr;
          this.fetchWeather(lat, lng);
          this.saveAccidentLocation(this.accidentCoords, this.accidentLabel);
        }
      } catch (e) {
        console.error("Alarm geocoding failed", e);
      } finally {
        this.loadingGeocode = false;
      }
    },
    resetToAlarmAddress() {
      if (this.originalAlarmCoords) {
        this.accidentCoords = [...this.originalAlarmCoords];
        this.accidentLabel = this.activeAlarm?.address || "Hälytysosoite";
        this.fetchWeather(this.accidentCoords[0], this.accidentCoords[1]);
        this.saveAccidentLocation(this.accidentCoords, this.accidentLabel);
      }
    },
    async searchAddress() {
      if (!this.searchQuery || !this.searchQuery.trim()) return;
      this.loadingGeocode = true;
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(
            this.searchQuery.trim(),
          )}`,
        );
        const data = await res.json();
        if (data && data.length > 0) {
          const lat = parseFloat(data[0].lat);
          const lng = parseFloat(data[0].lon);
          this.accidentCoords = [lat, lng];
          this.accidentLabel = data[0].display_name || this.searchQuery;
          this.fetchWeather(lat, lng);
          this.saveAccidentLocation(this.accidentCoords, this.accidentLabel);
        } else {
          alert("Osoitetta ei löytynyt kartalta.");
        }
      } catch (e) {
        console.error("Geocoding failed", e);
      } finally {
        this.loadingGeocode = false;
      }
    },
    useCurrentLocation() {
      if (!navigator.geolocation) {
        alert("Selain ei tue GPS-paikannusta.");
        return;
      }
      this.loadingLocation = true;
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          this.loadingLocation = false;
          const lat = pos.coords.latitude;
          const lng = pos.coords.longitude;
          this.accidentCoords = [lat, lng];
          this.accidentLabel = "Oma sijainti (GPS)";
          this.fetchWeather(lat, lng);
          this.saveAccidentLocation(this.accidentCoords, this.accidentLabel);
        },
        (err) => {
          this.loadingLocation = false;
          console.error("Geolocation error", err);
          alert("Sijainnin haku epäonnistui.");
        },
        { timeout: 10000, enableHighAccuracy: true },
      );
    },
    onMapClick(e) {
      if (e && e.latlng) {
        this.pendingAccidentCoords = [e.latlng.lat, e.latlng.lng];
        this.showMoveConfirm = true;
      }
    },
    onMarkerDragEnd(e) {
      if (e && e.target) {
        const latlng = e.target.getLatLng();
        this.pendingAccidentCoords = [latlng.lat, latlng.lng];
        this.showMoveConfirm = true;
      }
    },
    confirmMoveAccident() {
      if (this.pendingAccidentCoords && this.pendingAccidentCoords.length === 2) {
        this.accidentCoords = [...this.pendingAccidentCoords];
        this.accidentLabel = "Valittu kartalta";
        this.fetchWeather(this.accidentCoords[0], this.accidentCoords[1]);
        this.saveAccidentLocation(this.accidentCoords, this.accidentLabel);
      }
      this.cancelMoveAccident();
    },
    cancelMoveAccident() {
      this.showMoveConfirm = false;
      this.pendingAccidentCoords = null;
      // In case marker was dragged, reset marker back to confirmed accidentCoords
      this.$nextTick(() => {
        if (this.$refs.accidentMarker && this.$refs.accidentMarker.mapObject) {
          this.$refs.accidentMarker.mapObject.setLatLng(this.accidentCoords);
        }
      });
    },
    async fetchWeather(lat, lng) {
      if (!lat || !lng) return;
      this.loadingWeather = true;
      try {
        const res = await fetch(
          `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}&current=temperature_2m,wind_speed_10m,wind_direction_10m,wind_gusts_10m&wind_speed_unit=ms&timezone=auto`,
        );
        const data = await res.json();
        if (data && data.current) {
          this.windSpeed = data.current.wind_speed_10m;
          this.windDirection = data.current.wind_direction_10m;
          this.windGust = data.current.wind_gusts_10m;
          this.temperature = data.current.temperature_2m;
          this.weatherFetchedAt = dayjs().format("HH:mm:ss");
          this.weatherLocationName = this.accidentLabel || `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
        }
      } catch (e) {
        console.error("Live weather fetch failed", e);
      } finally {
        this.loadingWeather = false;
      }
    },
    openCustomDimensionsDialog() {
      this.editInner = this.currentRadius.inner;
      this.editOuter = this.currentRadius.outer;
      this.showDimensionsDialog = true;
    },
    applyCustomDimensions() {
      const inner = Math.max(5, parseInt(this.editInner, 10) || 25);
      const outer = Math.max(inner, parseInt(this.editOuter, 10) || inner);
      this.customInner = inner;
      this.customOuter = outer;
      this.isCustomDimensions = true;
      this.showDimensionsDialog = false;
    },
    resetToTokevaDimensions() {
      this.isCustomDimensions = false;
      this.showDimensionsDialog = false;
    },
  },
};
</script>

<style scoped lang="scss">
.tokeva-danger-zone-card {
  .svg-wrapper {
    width: 100%;
    max-width: 500px;
    margin: 0 auto;
  }

  .danger-zone-svg {
    width: 100%;
    height: auto;
    display: block;
    filter: drop-shadow(0 2px 6px rgba(0, 0, 0, 0.12));
  }

  /* High contrast leak toggle buttons */
  .leak-toggle-group {
    border: 1px solid rgba(0, 0, 0, 0.18) !important;
    border-radius: 4px;
    overflow: hidden;

    .leak-btn {
      letter-spacing: 0.2px;
      transition: all 0.2s ease;
      background-color: #f1f3f5 !important;
      color: #1a1a1a !important;
      opacity: 1 !important;

      .v-icon {
        color: inherit !important;
      }

      &.leak-btn-small.leak-btn-active {
        background-color: #0d47a1 !important;
        color: #ffffff !important;
      }

      &.leak-btn-large.leak-btn-active {
        background-color: #b71c1c !important;
        color: #ffffff !important;
      }
    }
  }

  /* Tactical Zone Cards */
  .border-red-zone {
    border-left: 5px solid #c62828 !important;
    background-color: #ffebee;
  }
  .border-yellow-zone {
    border-left: 5px solid #f57f17 !important;
    background-color: #fffde7;
  }
  .border-green-zone {
    border-left: 5px solid #2e7d32 !important;
    background-color: #e8f5e9;
  }
}

/* Ultra-compact floating tactical wind compass widget in Leaflet container */
.map-floating-wind-indicator {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 1000;
  background-color: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(0, 0, 0, 0.15);
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.15);
  pointer-events: auto;
  user-select: none;

  &.is-fullscreen-wind {
    top: calc(max(10px, env(safe-area-inset-top, 10px)) + 46px);
    right: 10px;
  }

  .wind-compass-dial {
    width: 22px;
    height: 22px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.4s ease;

    .mdi {
      font-size: 20px;
    }
  }

  .wind-label-tiny {
    font-size: 7.5px !important;
    letter-spacing: 0.5px;
    line-height: 1;
  }

  .wind-speed-text {
    font-size: 11px !important;
    line-height: 1.1;
  }
}

/* Inline map floating fullscreen button */
.map-floating-fullscreen-btn {
  position: absolute;
  bottom: 12px;
  left: 12px;
  z-index: 1000;
  pointer-events: auto;
  background-color: rgba(255, 255, 255, 0.94) !important;
  backdrop-filter: blur(4px);
  border: 1px solid rgba(0, 0, 0, 0.15);
  letter-spacing: 0.2px;
}

/* Floating confirmation banner placed high on the page (top of map) */
.map-confirm-move-banner {
  position: absolute;
  top: 12px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1002;
  background-color: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(8px);
  border: 1.5px solid #f57c00;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.25);
  max-width: 460px;
  width: calc(100% - 24px);
  pointer-events: auto;
  box-sizing: border-box;
}

/* Custom pending location marker icon */
::v-deep .pending-map-icon {
  background: none;
  border: none;

  .pending-pulse-wrapper {
    position: relative;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;

    .pending-pulse-ring {
      position: absolute;
      width: 36px;
      height: 36px;
      border-radius: 50%;
      border: 3px solid #ff9800;
      animation: pulse-hazard 1.4s infinite ease-out;
    }

    .pending-marker-core {
      width: 26px;
      height: 26px;
      border-radius: 50%;
      background-color: #f57c00;
      border: 2px solid #ffffff;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.4);

      .mdi {
        font-size: 16px;
        color: #ffffff;
      }
    }
  }
}

/* Inline map stacking isolation: guarantees Leaflet layers (markers 600, popups 700, controls 1000)
   never escape this local stacking context or punch through Vuetify modals and dialogs */
.map-view-container:not(.map-fullscreen-overlay) {
  position: relative;
  z-index: 1;
  isolation: isolate;
}

.map-wrapper {
  position: relative;
  z-index: 1;
  isolation: isolate;
  height: 620px;
  min-height: 520px;

  @media (min-width: 960px) {
    height: 720px;
  }

  @media (min-width: 1400px) {
    height: 780px;
  }
}

/* Fullscreen modal overlay styling - True 100vw x 100vh modal layout */
.map-fullscreen-overlay {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  width: 100vw !important;
  height: 100vh !important;
  height: 100dvh !important; /* dynamic viewport height for mobile browsers */
  z-index: 100 !important;
  background: #181a1f !important;
  display: block !important;
  margin: 0 !important;
  padding: 0 !important;
  overflow: hidden !important;

  .map-wrapper {
    position: absolute !important;
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    width: 100% !important;
    height: 100% !important;
    border-radius: 0 !important;
    min-height: 0 !important;
  }

  /* Reposition Leaflet zoom controls so they don't collide with top controls */
  ::v-deep .leaflet-top.leaflet-left {
    top: calc(max(10px, env(safe-area-inset-top, 10px)) + 50px) !important;
    left: 10px !important;
  }

  /* Make Leaflet attribution unobtrusive in fullscreen */
  ::v-deep .leaflet-control-attribution {
    font-size: 8px !important;
    opacity: 0.65;
    background: rgba(255, 255, 255, 0.7) !important;
  }
}

/* Floating Top Controls in Fullscreen */
.fullscreen-floating-top {
  position: absolute;
  top: max(10px, env(safe-area-inset-top, 10px));
  left: 10px;
  right: 10px;
  z-index: 1001;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: space-between;

  > * {
    pointer-events: auto;
  }

  .fullscreen-close-btn {
    min-height: 36px;
    letter-spacing: 0.4px;
    box-shadow: 0 4px 14px rgba(211, 47, 47, 0.45) !important;
  }

  .fullscreen-tactical-pill {
    background-color: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(8px);
    border: 1px solid rgba(0, 0, 0, 0.12);
    min-height: 34px;
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.18);
  }
}

/* Floating Search Dropdown in Fullscreen */
.fullscreen-search-overlay {
  position: absolute;
  top: calc(max(10px, env(safe-area-inset-top, 10px)) + 48px);
  left: 12px;
  right: 12px;
  max-width: 440px;
  margin: 0 auto;
  z-index: 1003;
  background-color: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(0, 0, 0, 0.18);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
  pointer-events: auto;
}

/* Floating Bottom Dock in Fullscreen (Thumb-friendly mobile dock) */
.fullscreen-floating-bottom {
  position: absolute;
  bottom: max(12px, env(safe-area-inset-bottom, 12px));
  left: 50%;
  transform: translateX(-50%);
  z-index: 1001;
  pointer-events: none;
  width: auto;
  max-width: calc(100vw - 20px);

  > * {
    pointer-events: auto;
  }

  .fullscreen-bottom-dock {
    background-color: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(0, 0, 0, 0.15);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
  }
}

/* Custom Leaflet Marker Icons */
::v-deep .accident-map-icon {
  background: none;
  border: none;

  .accident-pulse-wrapper {
    position: relative;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .accident-pulse-ring {
    position: absolute;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background-color: rgba(211, 47, 47, 0.45);
    animation: pulse-hazard 1.8s infinite;
  }

  .accident-marker-core {
    position: relative;
    z-index: 2;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background-color: #b71c1c;
    border: 2px solid #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.4);

    .mdi {
      font-size: 18px;
      color: #ffffff;
    }
  }
}

@keyframes pulse-hazard {
  0% {
    transform: scale(0.8);
    opacity: 1;
  }
  100% {
    transform: scale(2.2);
    opacity: 0;
  }
}

::v-deep .approach-map-icon {
  background: none !important;
  border: none !important;
  overflow: visible !important;

  .approach-marker-badge {
    background-color: #2e7d32;
    color: #ffffff;
    padding: 2px 7px;
    border-radius: 10px;
    font-size: 9.5px;
    font-weight: 700;
    letter-spacing: 0.1px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 1.5px 4px rgba(0, 0, 0, 0.3);
    border: 1px solid #ffffff;
    white-space: nowrap;
    width: max-content !important;
    max-width: none !important;
    box-sizing: border-box;
    line-height: 1.2;

    .mdi {
      font-size: 13px;
      margin-right: 2px;
    }
  }
}

::v-deep .approach-direction-map-icon {
  background: none !important;
  border: none !important;

  .approach-arrow-wrapper {
    width: 26px;
    height: 26px;
    display: flex;
    align-items: center;
    justify-content: center;

    .approach-arrow-circle {
      width: 24px;
      height: 24px;
      border-radius: 50%;
      background-color: #2e7d32;
      border: 2px solid #ffffff;
      color: #ffffff;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.35);

      .mdi {
        font-size: 15px;
      }
    }
  }
}

::v-deep .downwind-map-icon {
  background: none !important;
  border: none !important;
  overflow: visible !important;

  .downwind-warning-badge {
    background-color: #d32f2f;
    color: #ffffff;
    padding: 2px 7px;
    border-radius: 10px;
    font-size: 9px;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 1.5px 4px rgba(0, 0, 0, 0.3);
    border: 1px solid #ffffff;
    white-space: nowrap;
    width: max-content !important;
    max-width: none !important;
    box-sizing: border-box;
    line-height: 1.2;

    .mdi {
      font-size: 12px;
      margin-right: 2px;
    }
  }
}

::v-deep .wind-direction-map-icon {
  background: none;
  border: none;

  .wind-arrow-wrapper {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;

    .wind-arrow-circle {
      width: 28px;
      height: 28px;
      border-radius: 50%;
      background-color: #0288d1;
      border: 2px solid #ffffff;
      color: #ffffff;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.35);

      .mdi {
        font-size: 18px;
      }
    }
  }
}

/* Theme Dark Adjustments */
.theme--dark {
  .tokeva-danger-zone-card {
    .border-red-zone {
      background-color: #2a1515 !important;
      border-color: #521c1c !important;
      border-left: 5px solid #e53935 !important;
      color: #ffcdd2;
    }
    .border-yellow-zone {
      background-color: #2a2512 !important;
      border-color: #544315 !important;
      border-left: 5px solid #fbc02d !important;
      color: #fff9c4;
    }
    .border-green-zone {
      background-color: #142817 !important;
      border-color: #1e4a23 !important;
      border-left: 5px solid #43a047 !important;
      color: #c8e6c9;
    }
    .address-tools-card,
    .tactical-distance-bar {
      background-color: #21252b !important;
    }
    .leak-toggle-group {
      border-color: rgba(255, 255, 255, 0.2) !important;

      .leak-btn {
        background-color: #2a2e39 !important;
        color: #e5e7eb !important;

        &.leak-btn-small.leak-btn-active {
          background-color: #1565c0 !important;
          color: #ffffff !important;
        }

        &.leak-btn-large.leak-btn-active {
          background-color: #c62828 !important;
          color: #ffffff !important;
        }
      }
    }
  }

  .map-floating-wind-indicator {
    background-color: rgba(28, 32, 40, 0.95);
    border-color: rgba(255, 255, 255, 0.15);
    color: #ffffff;

    .wind-label-tiny {
      color: #b0bec5 !important;
    }
    .wind-speed-text {
      color: #90caf9 !important;
    }
  }

  .map-floating-fullscreen-btn {
    background-color: rgba(28, 32, 40, 0.94) !important;
    border-color: rgba(255, 255, 255, 0.15) !important;
    color: #ffffff !important;
  }

  .map-confirm-move-banner {
    background-color: rgba(30, 33, 40, 0.98) !important;
    border-color: #ff9800 !important;
    color: #ffffff !important;

    .black--text {
      color: #ffffff !important;
    }
    .grey--text.text--darken-2 {
      color: #b0bec5 !important;
    }
  }

  .map-fullscreen-overlay {
    background: #121417 !important;

    .fullscreen-tactical-pill {
      background-color: rgba(28, 32, 40, 0.95);
      border-color: rgba(255, 255, 255, 0.15);
      color: #ffffff;
    }

    .fullscreen-search-overlay {
      background-color: rgba(28, 32, 40, 0.98);
      border-color: rgba(255, 255, 255, 0.2);
    }

    .fullscreen-bottom-dock {
      background-color: rgba(28, 32, 40, 0.95);
      border-color: rgba(255, 255, 255, 0.15);
    }

    ::v-deep .leaflet-control-attribution {
      background: rgba(20, 24, 30, 0.8) !important;
      color: #b0bec5 !important;
      a {
        color: #90caf9 !important;
      }
    }
  }
}
</style>
